// AuthorizationUtilizationReport.jsx - Track auth hours usage
import React, { useState, useEffect, useContext, useCallback } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { auditApi } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";

const AuthorizationUtilizationReport = () => {
  const { user } = useContext(AuthContext);

  const tenant_id = user?.tenant_id;

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({
    customer_id: "",
    status_filter: "all", // all, approaching_limit, unused, over_utilized, low_usage
    sort_by: "utilization_desc",
  });
  const [customers, setCustomers] = useState([]);

  // Load filter options
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const customerRes = await secureAxios.get("/api/customers", {
          params: { tenant_id },
        });
        setCustomers(customerRes.data || []);
      } catch (err) {
        console.error("Error loading filter options:", err);
      }
    };
    if (tenant_id) loadFilterOptions();
  }, [tenant_id]);

  // Load report data
  const loadReport = useCallback(async () => {
    if (!tenant_id) return;
    setLoading(true);
    try {
      const response = await secureAxios.get(
        "/api/business-reports/auth-utilization",
        {
          params: {
            tenant_id,
            customer_id: filters.customer_id || undefined,
            status_filter: filters.status_filter,
            sort_by: filters.sort_by,
          },
        }
      );

      if (response.data.success) {
        setReportData(response.data.data || []);
        setSummary(response.data.summary || null);

        await auditApi.logEntry({
          action: "GENERATE_AUTH_UTILIZATION_REPORT",
          entity_type: "REPORT",
          entity_id: null,
          contains_phi: true,
          action_status: "SUCCESS",
          purpose_of_use: "OPERATIONS",
        });
      }
    } catch (err) {
      console.error("Error loading auth utilization report:", err);
      toast.error("Failed to load authorization utilization report");
    } finally {
      setLoading(false);
    }
  }, [tenant_id, filters]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const formatNumber = (num, decimals = 1) => {
    return Number(num || 0).toFixed(decimals);
  };

  const getUtilizationColor = (pct, isFlatRate = false) => {
    if (isFlatRate) {
      // Flat rate: green for complete (any work done), gray for unused
      return pct > 0 ? "success" : "secondary";
    }
    // Regular services
    if (pct > 100) return "danger";      // Over limit
    if (pct === 100) return "success";   // Complete/Fully utilized
    if (pct >= 80) return "warning";     // Approaching
    if (pct > 0) return "info";          // In progress
    return "secondary";                   // Unused
  };

  const getUtilizationLabel = (pct, isFlatRate = false) => {
    if (isFlatRate) {
      // Flat rate: Complete or Unused
      return pct > 0 ? "Complete" : "Unused";
    }
    // Regular services
    if (pct > 100) return "Over Limit";
    if (pct === 100) return "Complete";
    if (pct >= 80) return "Approaching Limit";
    if (pct > 0) return "Low Usage";
    return "Unused";
  };

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            <i className="bi bi-pie-chart me-2"></i>
            Authorization Utilization Report
          </h2>
          <p className="text-muted mb-0">
            Track authorization hours usage and identify at-risk auths
          </p>
        </div>
        <button
          className="btn btn-outline-primary"
          onClick={loadReport}
          disabled={loading}
        >
          <i className="bi bi-arrow-clockwise me-1"></i>
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-4">
              <label className="form-label">Customer</label>
              <select
                className="form-select"
                value={filters.customer_id}
                onChange={(e) =>
                  setFilters({ ...filters, customer_id: e.target.value })
                }
              >
                <option value="">All Customers</option>
                {customers.map((c) => (
                  <option key={c.customer_id} value={c.customer_id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">Status Filter</label>
              <select
                className="form-select"
                value={filters.status_filter}
                onChange={(e) =>
                  setFilters({ ...filters, status_filter: e.target.value })
                }
              >
                <option value="all">All Authorizations</option>
                <option value="approaching_limit">
                  Approaching Limit (80%+)
                </option>
                <option value="over_utilized">Over Utilized (100%+)</option>
                <option value="unused">Unused (0%)</option>
                <option value="low_usage">Low Usage (&lt;50%)</option>
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">Sort By</label>
              <select
                className="form-select"
                value={filters.sort_by}
                onChange={(e) =>
                  setFilters({ ...filters, sort_by: e.target.value })
                }
              >
                <option value="utilization_desc">
                  Utilization (High to Low)
                </option>
                <option value="utilization_asc">
                  Utilization (Low to High)
                </option>
                <option value="hours_remaining">Hours Remaining</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="row g-3 mb-4">
          <div className="col-6 col-lg">
            <div className="card bg-primary text-white h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  Total Auths
                </h6>
                <h4 className="card-title mb-0">{summary.total_auths}</h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-lg">
            <div className="card bg-danger text-white h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  Over Limit
                </h6>
                <h4 className="card-title mb-0">{summary.over_utilized}</h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-lg">
            <div className="card bg-warning text-dark h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  Approaching (80%+)
                </h6>
                <h4 className="card-title mb-0">{summary.approaching_limit}</h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-lg">
            <div className="card bg-success text-white h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  Complete
                </h6>
                <h4 className="card-title mb-0">{summary.complete || 0}</h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-lg">
            <div className="card bg-secondary text-white h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">Unused</h6>
                <h4 className="card-title mb-0">{summary.unused}</h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-lg">
            <div className="card bg-info text-white h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  Avg Utilization
                </h6>
                <h4 className="card-title mb-0">
                  {formatNumber(summary.avg_utilization, 0)}%
                </h4>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {/* Data Table */}
      {!loading && reportData.length > 0 && (
        <div className="card">
          <div className="table-responsive">
            <table className="table table-striped table-hover mb-0">
              <thead className="table-dark">
                <tr>
                  <th>Auth Number</th>
                  <th>Consumer</th>
                  <th>Customer</th>
                  <th>Service</th>
                  <th className="text-end">Auth Hours</th>
                  <th className="text-end">Used</th>
                  <th className="text-end">Remaining</th>
                  <th className="text-center" style={{ width: "150px" }}>
                    Utilization
                  </th>
                  <th className="text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {reportData.map((row, index) => {
                  const utilColor = getUtilizationColor(row.utilization_pct, row.is_flat_rate);
                  const utilLabel = getUtilizationLabel(row.utilization_pct, row.is_flat_rate);
                  return (
                    <tr key={index}>
                      <td className="fw-bold">{row.authorization_number}</td>
                      <td>{row.consumer_name}</td>
                      <td className="text-muted small">{row.customer_name}</td>
                      <td className="text-muted small">
                        {row.service_name}
                        {row.is_flat_rate && (
                          <span className="badge bg-info ms-1" style={{ fontSize: '0.65em' }}>
                            Flat Rate
                          </span>
                        )}
                      </td>
                      <td className="text-end">
                        {formatNumber(row.auth_hours, 0)}
                      </td>
                      <td className="text-end">
                        {formatNumber(row.hours_used, 1)}
                      </td>
                      <td className="text-end fw-bold">
                        {formatNumber(row.hours_remaining, 1)}
                      </td>
                      <td>
                        <div className="progress" style={{ height: "20px" }}>
                          <div
                            className={`progress-bar bg-${utilColor}`}
                            role="progressbar"
                            style={{
                              width: `${Math.min(row.utilization_pct, 100)}%`,
                            }}
                          >
                            {row.utilization_pct > 20 &&
                              `${formatNumber(row.utilization_pct, 0)}%`}
                          </div>
                        </div>
                        {row.utilization_pct <= 20 && (
                          <small className="text-muted">
                            {formatNumber(row.utilization_pct, 0)}%
                          </small>
                        )}
                      </td>
                      <td className="text-center">
                        <span className={`badge bg-${utilColor}`}>
                          {utilLabel}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* No Data */}
      {!loading && reportData.length === 0 && (
        <div className="text-center py-5">
          <i
            className="bi bi-inbox text-muted"
            style={{ fontSize: "3rem" }}
          ></i>
          <h5 className="mt-3">No Data Available</h5>
          <p className="text-muted">
            No authorizations found matching your criteria.
          </p>
        </div>
      )}
    </div>
  );
};

export default AuthorizationUtilizationReport;